import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { getMyVenues } from '../services/venueService';

const initial = { email: '', firstName: '', lastName: '', password: '', venueIds: [] };

export default function VenueOrganiserManagement() {
  const [form, setForm] = useState(initial);
  const [venues, setVenues] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [searchState, setSearchState] = useState('idle');
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  useEffect(() => {
    const query = form.email.trim();
    let active = true;
    setSuggestions([]); setActiveSuggestion(-1); setSearchState('idle');
    if (query.length < 2 || selectedPlayer?.email === query) return;
    const timer = window.setTimeout(async () => {
      setSearchState('loading');
      try {
        const { data } = await api.get('/users/search', { params: { q: query } });
        if (active) { setSuggestions(data.users || []); setSearchState('done'); }
      } catch {
        if (active) setSearchState('error');
      }
    }, 300);
    return () => { active = false; window.clearTimeout(timer); };
  }, [form.email, selectedPlayer]);
  function selectPlayer(player) {
    setForm(current => ({ ...current, email: player.email, firstName: '', lastName: '', password: '' }));
    setSelectedPlayer(player); setSuggestions([]); setSuggestionsOpen(false); setActiveSuggestion(-1);
  }
  function navigateSuggestions(event) {
    if (event.key === 'Escape') { setSuggestionsOpen(false); return; }
    if (event.key === 'ArrowDown' && suggestions.length) {
      event.preventDefault(); setSuggestionsOpen(true); setActiveSuggestion(index => Math.min(index + 1, suggestions.length - 1));
    } else if (event.key === 'ArrowUp' && suggestions.length) {
      event.preventDefault(); setActiveSuggestion(index => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && suggestionsOpen && activeSuggestion >= 0) {
      event.preventDefault(); selectPlayer(suggestions[activeSuggestion]);
    }
  }
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [venueList, response] = await Promise.all([getMyVenues(), api.get('/venue-organisers')]);
      setVenues(Array.isArray(venueList) ? venueList : []);
      setMemberships(response.data);
    } catch (e) { setError(e.response?.data?.message || 'Could not load organiser access.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  async function submit(e) {
    e.preventDefault();
    if (!form.venueIds.length) { toast.error('Select at least one venue.'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/venue-organisers', form);
      toast.success(data.message); setForm(initial); setSelectedPlayer(null); setSuggestionsOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not grant organiser access.'); }
    finally { setSaving(false); }
  }
  async function toggle(member) {
    if (member.isActive && !window.confirm(`Revoke ${member.firstName}'s Open Play access to ${member.venueName}?`)) return;
    setSaving(true);
    try {
      await api.patch(`/venue-organisers/${member.id}/status`, { isActive: !member.isActive });
      toast.success(member.isActive ? 'Access revoked.' : 'Access restored.'); await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not update access.'); }
    finally { setSaving(false); }
  }
  return <section className="card p-5 space-y-5">
    <div><h2 className="text-lg font-bold text-[#264D59]">Open Play organisers</h2>
      <p className="mt-1 text-sm text-slate-600">Give an existing FREAC player access to run Open Play at selected venues in the Court Portal and mobile app. Bank details, payouts, booking management, and venue settings stay restricted.</p></div>
    {error && <div role="alert" className="rounded-lg bg-red-50 p-3 text-red-700">{error} <button type="button" className="underline" onClick={load}>Retry</button></div>}
    <form onSubmit={submit} className="space-y-4">
      <div className="relative">
        <label htmlFor="venue-organiser-email" className="block text-sm font-medium">FREAC account email</label>
        <input id="venue-organiser-email" className="input-field mt-1" type="email" required autoComplete="off" placeholder="Search by name or email"
          role="combobox" aria-autocomplete="list" aria-expanded={suggestionsOpen && suggestions.length > 0}
          aria-controls="venue-organiser-suggestions" aria-activedescendant={suggestionsOpen && activeSuggestion >= 0 ? `venue-organiser-option-${activeSuggestion}` : undefined}
          value={form.email} onFocus={() => setSuggestionsOpen(true)} onBlur={() => setSuggestionsOpen(false)} onKeyDown={navigateSuggestions}
          onChange={e => { setSelectedPlayer(null); setSuggestionsOpen(true); setForm({ ...form, email: e.target.value }); }}/>
        {selectedPlayer && <p className="mt-2 text-sm text-teal-700">Selected: {selectedPlayer.firstName} {selectedPlayer.lastName} · Existing FREAC account</p>}
        {suggestionsOpen && form.email.trim().length >= 2 && !selectedPlayer && searchState !== 'idle' && <div className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
          {searchState === 'loading' && <p className="p-3 text-sm text-slate-500" role="status">Searching FREAC accounts…</p>}
          {searchState === 'error' && <p className="p-3 text-sm text-slate-500" role="status">Search unavailable. You can still enter the full account email.</p>}
          {searchState === 'done' && !suggestions.length && <p className="p-3 text-sm text-slate-500" role="status">No matching account. Enter their email and use “Create their account” below if they are new to FREAC.</p>}
          <ul id="venue-organiser-suggestions" role="listbox" aria-label="FREAC account suggestions">
            {suggestions.map((player, index) => <li key={player.id} role="presentation"><button id={`venue-organiser-option-${index}`} role="option" aria-selected={index === activeSuggestion}
              type="button" className={`block w-full px-4 py-3 text-left text-sm hover:bg-teal-50 ${index === activeSuggestion ? 'bg-teal-50' : ''}`}
              onMouseDown={e => e.preventDefault()} onClick={() => selectPlayer(player)}>
              <span className="block font-medium text-slate-800">{player.firstName} {player.lastName}</span><span className="text-slate-500">{player.email}</span>
            </button></li>)}
          </ul>
        </div>}
      </div>
      <fieldset><legend className="mb-2 text-sm font-medium">Assigned venues</legend>
        <div className="max-h-48 overflow-y-auto space-y-2">{venues.map(v => <label key={v.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.venueIds.includes(v.id)} onChange={e => setForm({ ...form, venueIds: e.target.checked ? [...form.venueIds, v.id] : form.venueIds.filter(id => id !== v.id) })}/>{v.name}</label>)}</div>
        {!loading && !venues.length && <p className="text-sm text-slate-500">Create a venue before assigning organisers.</p>}
      </fieldset>
      <details className="rounded-lg border p-3"><summary className="cursor-pointer text-sm font-medium">New to FREAC? Create their account</summary>
        <p className="my-2 text-xs text-slate-500">For existing players, leave these fields blank. Their password and player history will not change. New users must verify their email before signing in.</p>
        <div className="grid gap-3 sm:grid-cols-3">{[['firstName','First name'],['lastName','Last name'],['password','Initial password']].map(([key,label]) => <label key={key} className="text-sm">{label}<input className="input-field mt-1" type={key === 'password' ? 'password' : 'text'} autoComplete={key === 'password' ? 'new-password' : 'off'} minLength={key === 'password' ? 8 : undefined} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })}/></label>)}</div>
      </details>
      <button className="btn-primary" disabled={loading || saving || !venues.length}>{saving ? 'Saving…' : 'Grant organiser access'}</button>
    </form>
    {loading ? <p className="text-sm text-slate-500">Loading organiser access…</p> : !memberships.length ? <p className="text-sm text-slate-500">No venue organisers assigned yet.</p> : <ul className="divide-y">{memberships.map(m => <li key={m.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div><p className="font-medium">{m.firstName} {m.lastName} · {m.venueName}</p><p className="text-sm text-slate-500">{m.email} · {m.isActive ? 'Active' : 'Revoked'}{!m.isEmailVerified ? ' · Email verification pending' : ''}</p></div>
      <button type="button" disabled={saving} className="btn-outline" onClick={() => toggle(m)}>{m.isActive ? 'Revoke access' : 'Restore access'}</button>
    </li>)}</ul>}
  </section>;
}
